import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { db, collection, getDocs, doc, deleteDoc } from '../../firebase/config';
import { Heart, Star, ShoppingBag, User, X } from 'lucide-react';

const FavoritesPage = () => {
  const { user } = useAuth();
  const [favorites, setFavorites] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadFavorites = async () => {
      try {
        const favQuery = collection(db, 'users', user.uid, 'favorites');
        const snapshot = await getDocs(favQuery);
        const favData = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        setFavorites(favData);
      } catch (error) {
        console.error('Error loading favorites:', error);
      } finally {
        setLoading(false);
      }
    };

    if (user?.uid) {
      loadFavorites();
    }
  }, [user]);

  const removeFavorite = async (favId) => {
    try {
      await deleteDoc(doc(db, 'users', user.uid, 'favorites', favId));
      setFavorites(favorites.filter(f => f.id !== favId));
    } catch (error) {
      console.error('Error removing favorite:', error);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-12 h-12 border-4 border-[#EFA9B6] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto">
      <div className="mb-8">
        <h1 className="text-3xl font-serif font-bold text-gray-900">Favorites</h1>
        <p className="text-gray-500 mt-1">Your saved professionals and products</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {favorites.map((fav) => (
          <div key={fav.id} className="bg-white rounded-2xl p-4 shadow-sm border border-gray-50 hover:shadow-md transition-all">
            <div className="flex items-start justify-between">
              <div className="flex items-start gap-3">
                <div className="w-12 h-12 rounded-xl bg-[#FFF5F6] flex items-center justify-center text-[#EFA9B6]">
                  {fav.type === 'professional' ? <User size={20} /> : <ShoppingBag size={20} />}
                </div>
                <div>
                  <h3 className="font-bold text-gray-900">{fav.name || 'Item'}</h3>
                  <p className="text-xs text-gray-500">{fav.category || 'Category'}</p>
                  {fav.rating && (
                    <div className="flex items-center gap-1 mt-1">
                      <Star size={12} className="fill-orange-400 text-orange-400" />
                      <span className="text-xs font-bold">{fav.rating}</span>
                    </div>
                  )}
                </div>
              </div>
              <button
                onClick={() => removeFavorite(fav.id)}
                className="p-1 hover:bg-red-50 rounded-full transition-colors"
              >
                <X size={16} className="text-gray-400 hover:text-red-500" />
              </button>
            </div>
            <button className="w-full mt-3 py-2 bg-[#FFF5F6] text-[#EFA9B6] text-sm font-bold rounded-xl hover:bg-[#EFA9B6] hover:text-white transition-colors">
              {fav.type === 'professional' ? 'Book Now' : 'Add to Cart'}
            </button>
          </div>
        ))}
        {favorites.length === 0 && (
          <div className="col-span-full text-center py-12">
            <div className="w-20 h-20 bg-[#FFF5F6] rounded-full flex items-center justify-center mx-auto mb-4">
              <Heart size={32} className="text-[#EFA9B6]" />
            </div>
            <h3 className="text-lg font-serif font-bold text-gray-900 mb-2">No favorites yet</h3>
            <p className="text-gray-400 text-sm">Start saving your favorite professionals and products</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default FavoritesPage;